import { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SectionTitle from '../../../components/atoms/SectionTitle';
import SectionDesc from '../../../components/atoms/SectionDesc';
import CustomModal from '../../../components/atoms/CustomModal';
import TitleText from '../../../components/atoms/TitleText';
import Input from '../../../components/atoms/Input';
import Button from '../../../components/atoms/Button';
import { CloseSquare } from 'iconsax-react';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import DurationSection from './DurationSection';
import { PromotionDuration } from '../../../types/promotionDurations';
import {
  MarketplaceScopeCode,
  PromotionTypeCode,
} from '../../../types/promotionSlots';
import {
  useGetPromotionDurationsQuery,
  useCreatePromotionDurationMutation,
  useUpdatePromotionDurationMutation,
  useDeletePromotionDurationMutation,
} from '../../../store/services/promotionDurationsService';

type ModalMode = 'add' | 'edit' | null;
type TabKey = 'home' | 'category';

export default function PromotionsDurationsPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  // Derive active tab from URL
  const activeTab: TabKey = location.pathname.endsWith('/category')
    ? 'category'
    : 'home';

  const activeScope: MarketplaceScopeCode =
    activeTab === 'home' ? 'MAIN_MARKETPLACE' : 'CATEGORY_MARKETPLACE';

  // Redirect bare /promotions-durations to /promotions-durations/home
  useEffect(() => {
    if (location.pathname === '/promotions-durations') {
      navigate('/promotions-durations/home', { replace: true });
    }
  }, [location.pathname, navigate]);

  const { data: heroBannerResponse, isLoading: isHeroLoading } =
    useGetPromotionDurationsQuery({
      marketplaceScopeCode: activeScope,
      promotionTypeCode: 'HERO_BANNER',
    });

  const { data: topProductsResponse, isLoading: isTopLoading } =
    useGetPromotionDurationsQuery({
      marketplaceScopeCode: activeScope,
      promotionTypeCode: 'TOP_PRODUCTS',
    });

  const { data: flashDealsResponse, isLoading: isFlashLoading } =
    useGetPromotionDurationsQuery({
      marketplaceScopeCode: activeScope,
      promotionTypeCode: 'FLASH_DEALS',
    });

  const [createPromotionDuration, { isLoading: isCreating }] =
    useCreatePromotionDurationMutation();
  const [updatePromotionDuration, { isLoading: isUpdating }] =
    useUpdatePromotionDurationMutation();
  const [deletePromotionDuration, { isLoading: isDeleting }] =
    useDeletePromotionDurationMutation();

  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [selectedItem, setSelectedItem] = useState<PromotionDuration | null>(
    null
  );
  const [deleteItem, setDeleteItem] = useState<PromotionDuration | null>(null);
  const [addCategory, setAddCategory] = useState<PromotionTypeCode | null>(
    null
  );

  const schema = z.object({
    duration: z.preprocess(
      (val) =>
        val === '' || val === undefined || val === null
          ? undefined
          : Number(val),
      z
        .number({
          required_error: t('components.validations.requiredField') as string,
          invalid_type_error: t(
            'components.validations.requiredField'
          ) as string,
        })
        .gt(0, t('components.validations.min1') as string)
    ),
    price: z.preprocess(
      (val) =>
        val === '' || val === undefined || val === null
          ? undefined
          : Number(val),
      z
        .number({
          required_error: t('components.validations.requiredField') as string,
          invalid_type_error: t(
            'components.validations.requiredField'
          ) as string,
        })
        .min(1, t('components.validations.min1') as string)
    ),
  });

  type FormData = z.infer<typeof schema>;

  const { control, handleSubmit, reset } = useForm<FormData>({
    mode: 'all',
    resolver: zodResolver(schema),
    defaultValues: {
      duration: undefined,
      price: undefined,
    },
  });

  const resetModal = () => {
    setModalMode(null);
    setSelectedItem(null);
    setAddCategory(null);
    reset({
      duration: undefined,
      price: undefined,
    });
  };

  const handleAdd = (category: PromotionTypeCode) => {
    setModalMode('add');
    setAddCategory(category);
    reset({
      duration: undefined,
      price: undefined,
    });
  };

  const handleEdit = (item: PromotionDuration) => {
    setModalMode('edit');
    setSelectedItem(item);
    reset({
      duration: item.duration,
      price: item.price,
    });
  };

  const handleDeletePrompt = (item: PromotionDuration) => {
    setDeleteItem(item);
  };

  const handleConfirmDelete = async () => {
    if (!deleteItem) return;
    try {
      await deletePromotionDuration(deleteItem.id).unwrap();
      message.success(
        t('components.deletedSuccessfully', 'Deleted successfully')
      );
      setDeleteItem(null);
    } catch (error: any) {
      const errData = error?.data;
      const errorDetail = errData?.errors
        ? (Object.values(errData.errors)[0] as string)
        : null;
      message.error(
        errorDetail ||
          errData?.message ||
          t('components.errorOccurred', 'Error deleting duration')
      );
    }
  };

  const onSubmit = async (data: FormData) => {
    try {
      if (modalMode === 'add') {
        if (!addCategory) return;
        const durationUnitCode = addCategory === 'FLASH_DEALS' ? 'HOUR' : 'DAY';
        await createPromotionDuration({
          duration: data.duration,
          durationUnitCode,
          marketplaceScopeCode: activeScope,
          price: data.price,
          promotionTypeCode: addCategory,
        }).unwrap();
        message.success(
          t('components.addedSuccessfully', 'Added successfully')
        );
      } else if (modalMode === 'edit' && selectedItem) {
        const durationUnitCode =
          selectedItem.durationUnitCode ||
          (selectedItem.promotionTypeCode === 'FLASH_DEALS' ? 'HOUR' : 'DAY');
        await updatePromotionDuration({
          id: selectedItem.id,
          body: {
            duration: data.duration,
            durationUnitCode,
            marketplaceScopeCode: selectedItem.marketplaceScopeCode,
            price: data.price,
            promotionTypeCode: selectedItem.promotionTypeCode,
          },
        }).unwrap();
        message.success(
          t('components.updatedSuccessfully', 'Updated successfully')
        );
      }
      resetModal();
    } catch (error: any) {
      const errData = error?.data;
      const errorDetail = errData?.errors
        ? (Object.values(errData.errors)[0] as string)
        : null;
      message.error(
        errorDetail ||
          errData?.message ||
          t('components.errorOccurred', 'Error saving duration')
      );
    }
  };

  const sortByDuration = (a: PromotionDuration, b: PromotionDuration) =>
    a.duration - b.duration;

  const heroBannerDurations: PromotionDuration[] = useMemo(() => {
    return (heroBannerResponse?.payload || []).slice().sort(sortByDuration);
  }, [heroBannerResponse]);

  const topProductsDurations: PromotionDuration[] = useMemo(() => {
    return (topProductsResponse?.payload || []).slice().sort(sortByDuration);
  }, [topProductsResponse]);

  const flashDealsDurations: PromotionDuration[] = useMemo(() => {
    return (flashDealsResponse?.payload || []).slice().sort(sortByDuration);
  }, [flashDealsResponse]);

  const modalTitle =
    modalMode === 'add'
      ? t('home.duration-add-title')
      : t('home.duration-edit-title');

  const isFlashDeals =
    addCategory === 'FLASH_DEALS' ||
    selectedItem?.promotionTypeCode === 'FLASH_DEALS' ||
    selectedItem?.durationUnitCode === 'HOUR' ||
    selectedItem?.unit === 'hours';

  const isSaving = isCreating || isUpdating;

  return (
    <div className="flex flex-col gap-6">
      {/* Page Header */}
      <div className="flex flex-col gap-1">
        <SectionTitle
          text={
            activeTab === 'home'
              ? t('home.home-page-promotions')
              : t('home.category-page-promotions')
          }
        />
        <SectionDesc text={t('home.promotions-durations-desc')} />
      </div>

      {/* Hero Banner Section */}
      <DurationSection
        title={t('home.duration-hero-banner')}
        description={t('home.duration-hero-banner-desc')}
        durationColumnTitle={t('home.duration-days')}
        data={heroBannerDurations}
        isLoading={isHeroLoading}
        activeTab={activeTab}
        onAdd={() => handleAdd('HERO_BANNER')}
        onEdit={handleEdit}
        onDelete={handleDeletePrompt}
      />

      {/* Top Products Section */}
      <DurationSection
        title={t('home.duration-top-products')}
        description={t('home.duration-top-products-desc')}
        durationColumnTitle={t('home.duration-days')}
        data={topProductsDurations}
        isLoading={isTopLoading}
        activeTab={activeTab}
        onAdd={() => handleAdd('TOP_PRODUCTS')}
        onEdit={handleEdit}
        onDelete={handleDeletePrompt}
      />

      {/* Flash Deals Section */}
      <DurationSection
        title={t('home.duration-flash-deals')}
        description={t('home.duration-flash-deals-desc')}
        durationColumnTitle={t('home.duration-hours')}
        data={flashDealsDurations}
        isLoading={isFlashLoading}
        activeTab={activeTab}
        onAdd={() => handleAdd('FLASH_DEALS')}
        onEdit={handleEdit}
        onDelete={handleDeletePrompt}
      />

      {/* Add / Edit Modal */}
      <CustomModal
        isOpen={modalMode !== null}
        onClose={resetModal}
        title={modalTitle}
        subTitle={t('home.promotions-durations-desc')}
        width={600}
        closeIconClassName="[&_.ant-modal-close]:!top-8"
      >
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-6 mt-4"
        >
          <Controller
            name="duration"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Input
                id="duration-value"
                label={
                  isFlashDeals
                    ? t('home.duration-hours')
                    : t('home.duration-days')
                }
                placeholder={t('home.set-number')}
                type="number"
                {...field}
                value={field.value ?? ''}
                status={error ? 'error' : undefined}
                helperText={error?.message}
                className="!rounded-xl"
              />
            )}
          />
          <Controller
            name="price"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Input
                id="price"
                label={
                  activeTab === 'home'
                    ? t('home.duration-main-price')
                    : t('home.duration-category-price')
                }
                placeholder={t('home.set-number')}
                type="number"
                {...field}
                value={field.value ?? ''}
                status={error ? 'error' : undefined}
                helperText={error?.message}
                className="!rounded-xl"
              />
            )}
          />
          <div className="flex justify-end gap-3 mt-2">
            <Button
              variant="outline"
              type="button"
              onClick={resetModal}
              className="rounded-xl w-40"
              disabled={isSaving}
            >
              {t('home.slots-cancel')}
            </Button>
            <Button
              type="submit"
              className="rounded-xl w-36"
              loading={isSaving}
            >
              {modalMode === 'add'
                ? t('home.duration-add')
                : t('home.slots-edit-btn')}
            </Button>
          </div>
        </form>
      </CustomModal>

      {/* Delete Confirmation Modal */}
      <CustomModal
        isOpen={!!deleteItem}
        onClose={() => setDeleteItem(null)}
        title={null}
        centered
        width={715}
        closable={false}
      >
        <div className="flex flex-col justify-center text-center gap-2 py-4">
          <div className="w-12 h-12 rounded-[14px] bg-[#FEF2F2] flex items-center justify-center self-center mb-2">
            <CloseSquare
              size={24}
              className="text-[#EF4444]"
              variant="Outline"
            />
          </div>

          <TitleText
            text={t('home.delete-item-title')}
            size="2xl"
            className="!font-bold !text-[#111928]"
          />

          <p className="text-gray-500 text-sm max-w-[420px] mx-auto leading-relaxed">
            {t('home.delete-item-desc')}
          </p>

          <div className="flex flex-col-reverse sm:flex-row items-center justify-center sm:justify-end gap-3 mt-6 w-full">
            <Button
              variant="outline"
              onClick={() => setDeleteItem(null)}
              className="w-full sm:w-auto rounded-xl px-8 py-2 font-semibold border border-gray-300 !bg-white !text-[#111928] hover:!bg-gray-50"
              disabled={isDeleting}
            >
              {t('home.cancel')}
            </Button>
            <Button
              variant="dark"
              onClick={handleConfirmDelete}
              className="w-full sm:w-auto rounded-xl px-12 py-2 font-semibold !bg-[#D92D20] hover:!bg-[#B91C1C] !text-white !border-[#D92D20]"
              loading={isDeleting}
            >
              {t('home.yes-delete')}
            </Button>
          </div>
        </div>
      </CustomModal>
    </div>
  );
}
